/**
 * Одна задача целиком — для её страницы.
 *
 * В адресе стоит ключ (`/issue/DEV-1421`): его печатают в коммитах, ветках и
 * чате, и подставлять туда uuid значило бы сделать ссылку непересылаемой. А
 * сервис ищет по ключу только кратко (`GetIssuesByKeys` отдаёт `IssueBrief`),
 * поэтому дорога до полной задачи в два шага: ключ → идентификатор → задача.
 */
import { issueApi, projectApi } from '@/api'
import { cdnUrl } from '@/api/cdn'

import type {
  EntitiesV1Label,
  V1EntityType,
  V1Issue,
  V1IssueType,
  V1Priority,
} from '@/api/clients/tracker-issue'

export interface Issue {
  id: string
  key: string
  projectId: string
  title: string
  descriptionMd: string
  type: V1IssueType | ''
  priority: V1Priority | ''
  columnId: string
  assigneeId: string
  assigneeName: string
  /** Ссылки на фото тех же двоих; пусто — экран рисует инициалы */
  assigneeAvatar: string
  reporterId: string
  reporterName: string
  reporterAvatar: string
  /** Родитель: у задачи это эпик, у подзадачи — задача */
  parentId: string
  parentKey: string
  dueDate: string
  createdAt: string
  updatedAt: string
  labels: { id: string; name: string; color: string }[]
  watcherIds: string[]
  commentCount: number
  attachmentCount: number
  /**
   * Сущность CRM, которой принадлежит задача: у задач, приехавших мостом из
   * сделок и клиентов, здесь тип и её идентификатор. У задачи, заведённой
   * человеком, пусто.
   */
  entityType: V1EntityType | ''
  entityId: string
}

const toIssue = (issue: V1Issue): Issue => ({
  id: issue.id ?? '',
  key: issue.key ?? '',
  projectId: issue.projectId ?? '',
  title: issue.title ?? '',
  descriptionMd: issue.descriptionMd ?? '',
  type: issue.type ?? '',
  priority: issue.priority ?? '',
  columnId: issue.columnId ?? '',
  assigneeId: issue.assigneeId ?? '',
  assigneeName: issue.assigneeName ?? '',
  assigneeAvatar: cdnUrl(issue.assigneeAvatarUrl),
  reporterId: issue.reporterId ?? '',
  reporterName: issue.reporterName ?? '',
  reporterAvatar: cdnUrl(issue.reporterAvatarUrl),
  parentId: issue.parentId ?? '',
  parentKey: issue.parentKey ?? '',
  dueDate: issue.dueDate ?? '',
  createdAt: issue.createdAt ?? '',
  updatedAt: issue.updatedAt ?? '',
  labels: (issue.labels ?? []).map((label) => ({
    id: label.id ?? '',
    name: label.name ?? '',
    color: label.color ?? '',
  })),
  watcherIds: issue.watcherIds ?? [],
  // Счётчики приходят строками: в protobuf это int64.
  commentCount: Number(issue.commentCount ?? 0),
  attachmentCount: Number(issue.attachmentCount ?? 0),
  entityType: issue.entityType && issue.entityType !== 'ENTITY_TYPE_UNSPECIFIED' ? issue.entityType : '',
  entityId: issue.entityId ?? '',
})

/** Пустой ответ — это «нет такой задачи», и экран разбирает его как 404 */
export const getIssueByKey = async (key: string): Promise<Issue | null> => {
  const { data } = await issueApi.issueServiceGetIssuesByKeys([key])
  const id = data.issues?.[0]?.id

  if (!id) return null

  const { data: full } = await issueApi.issueServiceGetIssue(id)

  return full.issue ? toIssue(full.issue) : null
}

/**
 * Правка поля из мета-панели. Пустые значения не отправляем вовсе там, где
 * сервер проверяет uuid: `parent_id` объявлен `optional string` с проверкой, и
 * пустая строка её не проходит. У `assignee_id` проверки нет — там пустая
 * строка законно означает «никто».
 */
export interface IssuePatch {
  title?: string
  descriptionMd?: string
  priority?: V1Priority
  assigneeId?: string
  dueDate?: string
  parentId?: string
  /** Кого редактор описания заметил в упоминаниях: сервис их подпишет */
  mentionedUserIds?: string[]
}

export const updateIssue = async (id: string, patch: IssuePatch): Promise<void> => {
  await issueApi.issueServiceUpdateIssue(id, {
    ...patch,
    // Срок — день, а сервер ждёт момент времени: берём начало дня в поясе
    // человека, иначе «сегодня» в Ханое уезжает на вчера.
    ...(patch.dueDate !== undefined
      ? { dueDate: patch.dueDate ? new Date(`${patch.dueDate}T00:00:00`).toISOString() : '' }
      : {}),
  })
}

export interface Label {
  id: string
  name: string
  color: string
}

const toLabel = (label: EntitiesV1Label): Label => ({
  id: label.id ?? '',
  name: label.name ?? '',
  color: label.color ?? '',
})

/** Метки заводятся в проекте, а на задачу только вешаются */
export const listLabels = async (projectId: string): Promise<Label[]> => {
  const { data } = await projectApi.projectServiceListLabels(projectId)

  return (data.labels ?? []).map(toLabel)
}

export const createLabel = async (
  projectId: string,
  name: string,
  color: string,
): Promise<Label | null> => {
  const { data } = await projectApi.projectServiceCreateLabel(projectId, { name, color })

  return data.label ? toLabel(data.label) : null
}

/**
 * Метки задачи ставятся списком целиком: отдельных «добавить» и «снять» у
 * сервиса нет, и это правильно — иначе два человека, снявшие по метке
 * одновременно, вернули бы друг другу свои.
 */
export const setLabels = async (issueId: string, labelIds: string[]): Promise<void> => {
  await issueApi.issueServiceSetIssueLabels(issueId, { labelIds })
}

/**
 * Подписка на задачу. Без `userId` сервис подписывает того, кто просит, —
 * этим и пользуется кнопка «Подписаться».
 */
export const setWatcher = async (
  issueId: string,
  watching: boolean,
  userId?: string,
): Promise<string[]> => {
  const { data } = await issueApi.issueServiceSetIssueWatcher(issueId, { watching, userId })

  return data.issue?.watcherIds ?? []
}
